import React, { useState, useEffect } from 'react';
import { getReports, resolveReport, banUser, grantAdmin, getBannedUsers, subscribeToBannedUsersData, getAllReportsForUser, unbanUser, revokeAdmin, subscribeToReportedSparks, resolveSparkReport, getSystemReports, resolveSystemReport, deleteSystemReport, subscribeToSystemReports, subscribeToSystemConfig, updateSystemConfig } from '../services/firestore.js';
import { Shield, Check, Slash, AlertTriangle, UserX, ChevronDown, ChevronUp, User, Trash2 } from 'lucide-react';

const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    if (timestamp.seconds) {
        return new Date(timestamp.seconds * 1000).toLocaleString();
    }
    return new Date(timestamp).toLocaleString();
};

export default function AdminDashboard({ user, onNavigate, onViewProfile }) {
    const [reports, setReports] = useState([]);
    const [reportedSparks, setReportedSparks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [bannedUsers, setBannedUsers] = useState([]);
    const [expandedUserId, setExpandedUserId] = useState(null);
    const [userReports, setUserReports] = useState({});
    const [systemReports, setSystemReports] = useState([]);
    const [systemConfig, setSystemConfig] = useState({ enableReporting: true });
    const [expandedSystemReportId, setExpandedSystemReportId] = useState(null);

    // Subscribe to System Config
    useEffect(() => {
        if (!user?.isSuperAdmin) return;
        return subscribeToSystemConfig(setSystemConfig);
    }, [user]);

    useEffect(() => {
        if (!user?.isAdmin) return;

        // Initial load for reports (standard fetch)
        loadReports();

        // Real-time subscription for banned users (to see appeals instantly)
        const unsubscribeBanned = subscribeToBannedUsersData((data) => {
            setBannedUsers(data);
        });

        // Real-time subscription for reported sparks
        const unsubscribeSparks = subscribeToReportedSparks((data) => {
            setReportedSparks(data);
        });

        return () => {
            if (unsubscribeBanned) unsubscribeBanned();
            if (unsubscribeSparks) unsubscribeSparks();
        };
    }, [user]);

    const loadReports = async () => {
        setLoading(true);
        const reportsData = await getReports();
        setReports(reportsData);

        // if (user?.isSuperAdmin) {
        //      System reports are now handled by real-time subscription below
        // }
        setLoading(false);
    };

    // Subscribe to System Reports (Super Admin Only)
    useEffect(() => {
        if (!user?.isSuperAdmin) return;

        const unsubscribe = subscribeToSystemReports((reports) => {
            setSystemReports(reports);
        });

        return () => unsubscribe();
    }, [user]);

    useEffect(() => {
        loadReports();
    }, [user]);

    const handleDismiss = async (reportId) => {
        try {
            await resolveReport(reportId);
            setReports(prev => prev.filter(r => r.id !== reportId));
        } catch (e) {
            console.error(e);
            alert("Failed to dismiss report");
        }
    };

    const handleBan = async (reportId, userId) => {
        if (window.confirm("Are you sure you want to BAN this user? This action prevents them from using the app.")) {
            try {
                await banUser(userId);
                await resolveReport(reportId);
                setReports(prev => prev.filter(r => r.id !== reportId));
                // Reload banned users list
                const bannedData = await getBannedUsers();
                setBannedUsers(bannedData);
                alert("User banned.");
            } catch (e) {
                console.error(e);
                alert("Failed to ban user");
            }
        }
    };

    const handleUnban = async (userId) => {
        if (window.confirm("Are you sure you want to UNBAN this user?")) {
            try {
                await unbanUser(userId);
                setBannedUsers(prev => prev.filter(u => u.id !== userId));
                alert("User unbanned.");
            } catch (e) {
                console.error(e);
                alert("Failed to unban user");
            }
        }
    };

    const handleToggleExpand = async (userId) => {
        if (expandedUserId === userId) {
            setExpandedUserId(null);
        } else {
            setExpandedUserId(userId);
            // Load reports for this user if not already loaded
            if (!userReports[userId]) {
                const reports = await getAllReportsForUser(userId);
                setUserReports(prev => ({ ...prev, [userId]: reports }));
            }
        }
    };

    const handleDemoteAdmin = async (reportId, userId) => {
        if (window.confirm("Are you sure you want to DEMOTE this admin? They will lose admin privileges.")) {
            try {
                await revokeAdmin(userId, user.uid);
                await resolveReport(reportId);
                setReports(prev => prev.filter(r => r.id !== reportId));
                alert("Admin demoted. You can now ban them if needed.");
            } catch (e) {
                console.error(e);
                alert(e.message || "Failed to demote admin");
            }
        }
    };

    const handleDismissSpark = async (sparkId) => {
        try {
            await resolveSparkReport(sparkId, 'dismiss');
            // State updates via subscription
        } catch (e) {
            console.error(e);
            alert("Failed to dismiss report.");
        }
    };

    const handleDeleteSpark = async (sparkId) => {
        if (window.confirm("Permanently delete this spark? This cannot be undone.")) {
            try {
                await resolveSparkReport(sparkId, 'delete');
            } catch (e) {
                console.error(e);
                alert("Failed to delete spark.");
            }
        }
    };

    const [viewAppealUser, setViewAppealUser] = useState(null);

    if (!user || !user.isAdmin) {
        return React.createElement('div', { className: 'glass-panel', style: { padding: '50px', textAlign: 'center' } }, 'Access Denied');
    }

    return React.createElement('div', { className: 'animate-fade-in', style: { maxWidth: '800px', margin: '0 auto', paddingBottom: '100px' } },
        React.createElement('div', { className: 'glass-panel', style: { padding: '30px', marginBottom: '20px' } },
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' } },
                React.createElement(Shield, { size: 32, color: '#8b5cf6' }),
                React.createElement('h1', { style: { margin: 0 } }, 'Admin Dashboard')
            ),
            React.createElement('p', { style: { opacity: 0.7 } }, 'Manage user reports and safety.')
        ),

        // Admin Tools Section (Super Admin Only)
        user.isSuperAdmin && React.createElement('div', { className: 'glass-panel', style: { padding: '20px', marginBottom: '20px' } },
            React.createElement('h2', { style: { marginBottom: '15px', fontSize: '18px' } }, '🛠️ Admin Tools'),
            React.createElement('div', { style: { display: 'flex', gap: '10px', flexWrap: 'wrap' } },

                React.createElement('button', {
                    onClick: () => onNavigate('manage-emotes'),
                    className: 'btn',
                    style: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', background: '#eab308' }
                },
                    React.createElement('span', { style: { fontSize: '20px' } }, '💡'),
                    'Manage Emotes'
                )
            ),

            // --- SYSTEM REPORTS PANEL ---
            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', marginBottom: '15px', borderTop: '1px solid var(--border)', paddingTop: '20px' } },
                React.createElement('h3', { style: { margin: 0 } }, 'System Reports (' + systemReports.length + ')'),
                React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '10px' } },
                    // EARLY ADOPTER TOGGLE
                    React.createElement('div', {
                        style: {
                            display: 'flex', alignItems: 'center', gap: '8px',
                            background: 'rgba(0,0,0,0.3)', padding: '6px 12px', borderRadius: '20px', mr: '10px'
                        }
                    },
                        React.createElement('span', { style: { fontSize: '12px', opacity: 0.7 } }, 'Early Adopter Badge'),
                        React.createElement('button', {
                            onClick: () => {
                                updateSystemConfig({ earlyAdopterOpen: !systemConfig.earlyAdopterOpen });
                            },
                            style: {
                                background: systemConfig.earlyAdopterOpen ? '#8b5cf6' : '#333',
                                borderRadius: '20px',
                                width: '40px',
                                height: '20px',
                                border: 'none',
                                position: 'relative',
                                cursor: 'pointer',
                                transition: 'background 0.3s'
                            }
                        },
                            React.createElement('div', {
                                style: {
                                    background: 'white',
                                    width: '14px',
                                    height: '14px',
                                    borderRadius: '50%',
                                    position: 'absolute',
                                    top: '3px',
                                    left: systemConfig.earlyAdopterOpen ? '23px' : '3px',
                                    transition: 'left 0.3s'
                                }
                            })
                        )
                    ),

                    React.createElement('span', { style: { fontSize: '12px', opacity: 0.7 } }, 'Enable Global Reporting'),
                    React.createElement('button', {
                        onClick: () => {
                            if (window.confirm(systemConfig.enableReporting ? 'Disable reporting form for all users?' : 'Enable reporting form for all users?')) {
                                updateSystemConfig({ enableReporting: !systemConfig.enableReporting });
                            }
                        },
                        style: {
                            background: systemConfig.enableReporting ? '#10b981' : '#333',
                            borderRadius: '20px',
                            width: '40px',
                            height: '24px',
                            border: 'none',
                            position: 'relative',
                            cursor: 'pointer',
                            transition: 'background 0.3s'
                        }
                    },
                        React.createElement('div', {
                            style: {
                                background: 'white',
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                position: 'absolute',
                                top: '4px',
                                left: systemConfig.enableReporting ? '20px' : '4px',
                                transition: 'left 0.3s'
                            }
                        })
                    )
                )
            ),
            systemReports.length === 0 ? (
                React.createElement('div', { style: { opacity: 0.5, fontStyle: 'italic' } }, "No system reports.")
            ) : (
                React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '10px' } },
                    systemReports.map(report => {
                        const isExpanded = expandedSystemReportId === report.id;
                        return React.createElement('div', { key: report.id, style: { background: 'rgba(0,0,0,0.2)', borderRadius: '8px', padding: '15px' } },
                            React.createElement('div', {
                                onClick: () => setExpandedSystemReportId(isExpanded ? null : report.id),
                                style: { display: 'flex', justifyContent: 'space-between', cursor: 'pointer', alignItems: 'center' }
                            },
                                React.createElement('div', { style: { display: 'flex', gap: '10px', alignItems: 'center' } },
                                    // Type Icon
                                    React.createElement('div', {
                                        style: {
                                            padding: '6px', borderRadius: '6px',
                                            background: report.type === 'bug' ? 'rgba(239,68,68,0.2)' : report.type === 'issue' ? 'rgba(245,158,11,0.2)' : 'rgba(59,130,246,0.2)',
                                            color: report.type === 'bug' ? '#ef4444' : report.type === 'issue' ? '#f59e0b' : '#3b82f6'
                                        }
                                    },
                                        report.type === 'bug' ? React.createElement(AlertTriangle, { size: 16 }) : report.type === 'issue' ? React.createElement(AlertTriangle, { size: 16 }) : React.createElement(Shield, { size: 16 })
                                    ),
                                    React.createElement('div', {},
                                        React.createElement('div', { style: { fontWeight: 'bold', textTransform: 'capitalize' } }, report.type),
                                        React.createElement('div', { style: { fontSize: '11px', opacity: 0.6 } },
                                            'By ',
                                            React.createElement('span', {
                                                onClick: (e) => {
                                                    e.stopPropagation();
                                                    if (report.reporterId) onViewProfile(report.reporterId);
                                                },
                                                style: {
                                                    cursor: report.reporterId ? 'pointer' : 'default',
                                                    textDecoration: report.reporterId ? 'underline' : 'none',
                                                    color: report.reporterId ? 'var(--primary)' : 'inherit'
                                                }
                                            }, report.reporterName || 'Unknown'),
                                            ' • ' + formatDate(report.timestamp)
                                        )
                                    )
                                ),
                                React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '10px' } },
                                    report.status === 'resolved' && React.createElement('span', { style: { fontSize: '10px', padding: '2px 6px', borderRadius: '4px', background: '#10b981', color: 'white' } }, 'Resolved'),
                                    React.createElement(isExpanded ? ChevronUp : ChevronDown, { size: 16 })
                                )
                            ),
                            // Expanded Content
                            isExpanded && React.createElement('div', { style: { marginTop: '15px', paddingTop: '15px', borderTop: '1px solid rgba(255,255,255,0.1)' } },
                                React.createElement('div', { style: { marginBottom: '10px' } },
                                    React.createElement('div', { style: { fontSize: '11px', fontWeight: 'bold', opacity: 0.7, marginBottom: '5px' } }, "DESCRIPTION"),
                                    React.createElement('div', { style: { whiteSpace: 'pre-wrap', fontSize: '13px' } }, report.description)
                                ),
                                report.reproduceSteps && React.createElement('div', { style: { marginBottom: '10px' } },
                                    React.createElement('div', { style: { fontSize: '11px', fontWeight: 'bold', opacity: 0.7, marginBottom: '5px' } }, "STEPS TO REPRODUCE"),
                                    React.createElement('div', { style: { whiteSpace: 'pre-wrap', fontSize: '13px' } }, report.reproduceSteps)
                                ),
                                report.imageUrl && React.createElement('div', { style: { marginBottom: '10px' } },
                                    React.createElement('div', { style: { fontSize: '11px', fontWeight: 'bold', opacity: 0.7, marginBottom: '5px' } }, "SCREENSHOT"),
                                    React.createElement('a', { href: report.imageUrl, target: "_blank", rel: "noopener noreferrer" },
                                        React.createElement('img', { src: report.imageUrl, style: { maxHeight: '200px', borderRadius: '8px', border: '1px solid var(--border)' } })
                                    )
                                ),
                                React.createElement('div', { style: { display: 'flex', gap: '10px', marginTop: '15px', justifyContent: 'flex-end' } },
                                    // Mark as Resolved
                                    report.status !== 'resolved' && React.createElement('button', {
                                        onClick: async () => {
                                            if (window.confirm('Mark as resolved?')) {
                                                await resolveSystemReport(report.id);
                                                setSystemReports(prev => prev.map(r => r.id === report.id ? { ...r, status: 'resolved' } : r));
                                            }
                                        },
                                        className: 'btn',
                                        style: { background: '#10b981', border: 'none', padding: '8px 15px', fontSize: '12px' }
                                    }, "Mark Resolved"),

                                    // Unresolve
                                    report.status === 'resolved' && React.createElement('button', {
                                        onClick: async () => {
                                            await resolveSystemReport(report.id, 'pending');
                                            setSystemReports(prev => prev.map(r => r.id === report.id ? { ...r, status: 'pending' } : r));
                                        },
                                        className: 'btn-ghost',
                                        style: { border: '1px solid var(--border)', padding: '8px 15px', fontSize: '12px' }
                                    }, "Unresolve"),

                                    // Delete
                                    React.createElement('button', {
                                        onClick: async () => {
                                            if (window.confirm('Permanently delete this report?')) {
                                                await deleteSystemReport(report.id);
                                                setSystemReports(prev => prev.filter(r => r.id !== report.id));
                                            }
                                        },
                                        className: 'btn-ghost',
                                        style: { border: '1px solid #ef4444', color: '#ef4444', padding: '8px 15px', fontSize: '12px' }
                                    }, React.createElement(Trash2, { size: 14 }))
                                )
                            )
                        );
                    })
                )
            )
        ),

        React.createElement('div', { className: 'glass-panel', style: { padding: '20px' } },
            React.createElement('h2', { style: { marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' } },
                React.createElement(AlertTriangle, { size: 20, color: '#f59e0b' }),
                `Pending Reports (${reports.length})`
            ),

            loading ? (
                React.createElement('div', { style: { padding: '20px', textAlign: 'center' } }, 'Loading reports...')
            ) : reports.length === 0 ? (
                React.createElement('div', { style: { padding: '40px', textAlign: 'center', color: '#10b981' } },
                    React.createElement(Check, { size: 48, style: { margin: '0 auto 10px', display: 'block' } }),
                    'No pending reports. All good!'
                )
            ) : (
                React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '15px' } },
                    reports.map(report =>
                        React.createElement('div', {
                            key: report.id,
                            style: {
                                background: 'rgba(255,255,255,0.05)',
                                borderRadius: '12px',
                                padding: '15px',
                                border: '1px solid var(--border)'
                            }
                        },
                            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' } },
                                React.createElement('div', { style: { display: 'flex', gap: '10px' } },
                                    React.createElement('img', {
                                        src: report.reportedAvatar || `https://api.dicebear.com/7.x/shapes/svg?seed=${report.reportedUserId}`,
                                        style: { width: '40px', height: '40px', borderRadius: '50%' }
                                    }),
                                    React.createElement('div', {},
                                        React.createElement('div', { style: { fontWeight: 'bold', color: '#ef4444' } }, report.reportedName),
                                        React.createElement('div', { style: { fontSize: '12px', opacity: 0.5 } }, `Reported by ${report.reporterName}`)
                                    )
                                ),
                                React.createElement('div', {
                                    style: {
                                        fontSize: '12px', opacity: 0.5
                                    }
                                },
                                    report.timestamp?.seconds ? new Date(report.timestamp.seconds * 1000).toLocaleDateString() : 'Just now'
                                )
                            ),

                            React.createElement('div', { style: { background: 'rgba(0,0,0,0.2)', padding: '10px', borderRadius: '8px', marginBottom: '15px', fontSize: '14px' } },
                                `"${report.reason}"`
                            ),

                            React.createElement('div', { style: { display: 'flex', gap: '10px', justifyContent: 'flex-end' } },
                                React.createElement('button', {
                                    onClick: () => handleDismiss(report.id),
                                    className: 'btn-ghost',
                                    style: {
                                        fontSize: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        background: '#4b5563', // Grey
                                        color: 'white',
                                        border: 'none',
                                        padding: '6px 12px',
                                        borderRadius: '6px'
                                    }
                                },
                                    React.createElement(Check, { size: 14 }),
                                    'Dismiss'
                                ),
                                // Check if reported user is super admin - no actions allowed
                                report.reportedIsSuperAdmin ? (
                                    React.createElement('div', { style: { fontSize: '11px', opacity: 0.6, fontStyle: 'italic' } },
                                        '🛡️ Super Admin (Protected)'
                                    )
                                ) : report.reportedIsAdmin ? (
                                    React.createElement('button', {
                                        onClick: () => handleDemoteAdmin(report.id, report.reportedUserId),
                                        className: 'btn',
                                        style: {
                                            background: '#f59e0b',
                                            border: 'none',
                                            fontSize: '12px',
                                            padding: '6px 12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '5px'
                                        }
                                    },
                                        React.createElement(Shield, { size: 14 }),
                                        'Demote Admin'
                                    )
                                ) : (
                                    React.createElement('button', {
                                        onClick: () => handleBan(report.id, report.reportedUserId),
                                        className: 'btn',
                                        style: {
                                            background: '#ef4444',
                                            border: 'none',
                                            fontSize: '12px',
                                            padding: '6px 12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '5px'
                                        }
                                    },
                                        React.createElement(UserX, { size: 14 }),
                                        'Ban User'
                                    )
                                )
                            )
                        )
                    )
                )
            )
        ),

        React.createElement('div', { className: 'glass-panel', style: { padding: '20px', marginBottom: '30px', border: '1px solid #ef4444' } },
            React.createElement('h2', { style: { marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px', color: '#ef4444' } },
                React.createElement(AlertTriangle, { size: 20 }),
                `Reported Sparks (${reportedSparks.length})`
            ),

            reportedSparks.length === 0 ? (
                React.createElement('div', { style: { padding: '20px', textAlign: 'center', opacity: 0.5 } }, 'No reported sparks.')
            ) : (
                React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '15px' } },
                    reportedSparks.map(spark =>
                        React.createElement('div', {
                            key: spark.id,
                            style: { background: 'rgba(0,0,0,0.2)', padding: '15px', borderRadius: '12px' }
                        },
                            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', marginBottom: '10px' } },
                                React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '10px' } },
                                    React.createElement('div', { style: { fontWeight: 'bold' } }, spark.author),
                                    React.createElement('div', { style: { fontSize: '12px', opacity: 0.5 } },
                                        spark.lastReportedAt?.seconds ? new Date(spark.lastReportedAt.seconds * 1000).toLocaleString() : 'Unknown'
                                    )
                                ),
                                React.createElement('div', { style: { color: '#ef4444', fontWeight: 'bold', fontSize: '12px' } },
                                    `${spark.reportedBy ? spark.reportedBy.length : 1} Report(s)`
                                )
                            ),
                            React.createElement('div', { style: { marginBottom: '15px', fontStyle: 'italic', padding: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px' } },
                                spark.content
                            ),
                            spark.imageUrl && React.createElement('img', { src: spark.imageUrl, style: { height: '100px', borderRadius: '8px', marginBottom: '15px' } }),
                            React.createElement('div', { style: { display: 'flex', gap: '10px', justifyContent: 'flex-end' } },
                                React.createElement('button', {
                                    onClick: () => handleDismissSpark(spark.id),
                                    className: 'btn-ghost',
                                    style: { fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px', background: '#4b5563', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px' }
                                }, React.createElement(Check, { size: 14 }), "Dismiss (Keep)"),
                                React.createElement('button', {
                                    onClick: () => handleDeleteSpark(spark.id),
                                    className: 'btn',
                                    style: { fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px', background: '#ef4444', border: 'none', padding: '6px 12px' }
                                }, React.createElement(Trash2, { size: 14 }), "Delete Spark")
                            )
                        )
                    )
                )
            )
        ),

        // Banned Users Section
        React.createElement('div', { className: 'glass-panel', style: { padding: '20px', marginTop: '30px' } },
            React.createElement('h2', { style: { marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' } },
                React.createElement(User, { size: 20, color: '#ef4444' }),
                `Banned Users (${bannedUsers.length})`
            ),

            bannedUsers.length === 0 ? (
                React.createElement('div', { style: { padding: '20px', textAlign: 'center', opacity: 0.5 } },
                    'No banned users'
                )
            ) : (
                React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '10px' } },
                    bannedUsers.map(bannedUser => {
                        const isExpanded = expandedUserId === bannedUser.id;
                        const reports = userReports[bannedUser.id] || [];
                        const banCount = bannedUser.banHistory?.filter(h => h.action === 'banned').length || 1;
                        const hasAppeal = bannedUser.banAppeal && bannedUser.banAppeal.text;

                        const isViewingAppeal = viewAppealUser?.id === bannedUser.id;

                        // Wrapper to hold Appeal + Card
                        return React.createElement('div', { key: bannedUser.id, style: { marginBottom: '10px' } },

                            // 1. INLINE APPEAL PANEL
                            isViewingAppeal && React.createElement('div', {
                                className: 'animate-slide-down',
                                style: {
                                    background: 'rgba(236, 72, 153, 0.15)',
                                    border: '1px solid #ec4899',
                                    borderBottom: 'none',
                                    borderTopLeftRadius: '12px',
                                    borderTopRightRadius: '12px',
                                    padding: '20px',
                                    marginBottom: '-5px',
                                    position: 'relative',
                                    zIndex: 1
                                }
                            },
                                React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' } },
                                    React.createElement('h3', { style: { margin: 0, color: '#ec4899', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' } },
                                        React.createElement('span', {}, "✋"),
                                        "Appeal Request"
                                    ),
                                    React.createElement('button', {
                                        onClick: () => setViewAppealUser(null),
                                        style: { background: 'none', border: 'none', color: '#ec4899', cursor: 'pointer', fontSize: '18px', padding: '0 5px', lineHeight: 1 }
                                    }, "×")
                                ),
                                React.createElement('div', {
                                    style: {
                                        background: 'rgba(0,0,0,0.3)',
                                        padding: '15px',
                                        borderRadius: '8px',
                                        fontStyle: 'italic',
                                        fontSize: '14px',
                                        marginBottom: '15px',
                                        borderLeft: '3px solid #ec4899'
                                    }
                                }, `"${bannedUser.banAppeal.text}"`),
                                React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
                                    React.createElement('span', { style: { fontSize: '11px', opacity: 0.6 } },
                                        `Submitted: ${formatDate(bannedUser.banAppeal.timestamp)}`
                                    ),
                                    React.createElement('button', {
                                        onClick: () => {
                                            handleUnban(bannedUser.id);
                                            setViewAppealUser(null);
                                        },
                                        className: 'btn',
                                        style: { background: '#10b981', padding: '6px 12px', fontSize: '12px', border: 'none' }
                                    }, "Approve & Unban")
                                )
                            ),

                            // 2. MAIN USER CARD (Inner Div)
                            React.createElement('div', {
                                style: {
                                    background: 'rgba(239,68,68,0.1)',
                                    borderRadius: isViewingAppeal ? '0 0 12px 12px' : '12px',
                                    padding: '15px',
                                    border: '1px solid rgba(239,68,68,0.3)',
                                    borderTop: isViewingAppeal ? '1px solid rgba(236, 72, 153, 0.5)' : '1px solid rgba(239,68,68,0.3)',
                                    transition: 'all 0.3s ease'
                                }
                            },
                                // Header
                                React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
                                    React.createElement('div', { style: { display: 'flex', gap: '10px', alignItems: 'center' } },
                                        React.createElement('img', {
                                            src: bannedUser.avatar || `https://api.dicebear.com/7.x/shapes/svg?seed=${bannedUser.id}`,
                                            style: { width: '40px', height: '40px', borderRadius: '50%', opacity: 0.5 }
                                        }),
                                        React.createElement('div', {},
                                            React.createElement('div', { style: { fontWeight: 'bold', color: '#ef4444' } }, bannedUser.name),
                                            React.createElement('div', { style: { fontSize: '11px', opacity: 0.7 } },
                                                `Banned ${banCount} time${banCount > 1 ? 's' : ''} • Last: ${formatDate(bannedUser.bannedAt)}`
                                            )
                                        )
                                    ),
                                    React.createElement('div', { style: { display: 'flex', gap: '10px', alignItems: 'center' } },

                                        // Appeal Button (Pink)
                                        hasAppeal && React.createElement('button', {
                                            onClick: () => setViewAppealUser(bannedUser),
                                            className: 'btn',
                                            style: {
                                                fontSize: '11px',
                                                padding: '6px 12px',
                                                background: '#ec4899', // Pink
                                                border: 'none',
                                                fontWeight: 'bold',
                                                boxShadow: '0 0 10px rgba(236, 72, 153, 0.4)',
                                                animation: 'pulse 2s infinite'
                                            }
                                        }, "Appeal Request"),

                                        React.createElement('button', {
                                            onClick: () => handleToggleExpand(bannedUser.id),
                                            className: 'btn-ghost',
                                            style: {
                                                fontSize: '11px',
                                                padding: '6px 10px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '5px',
                                                background: '#4b5563', // Grey
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '6px'
                                            }
                                        },
                                            React.createElement(isExpanded ? ChevronUp : ChevronDown, { size: 14 }),
                                            isExpanded ? 'Hide Details' : 'View Details'
                                        ),
                                        React.createElement('button', {
                                            onClick: () => handleUnban(bannedUser.id),
                                            className: 'btn',
                                            style: { fontSize: '11px', padding: '6px 12px', background: '#10b981', border: 'none' }
                                        },
                                            'Unban'
                                        )
                                    )
                                ),

                                // Expanded Details
                                isExpanded && React.createElement('div', { style: { marginTop: '15px', paddingTop: '15px', borderTop: '1px solid rgba(255,255,255,0.1)' } },
                                    // Ban History
                                    bannedUser.banHistory && bannedUser.banHistory.length > 0 && React.createElement('div', { style: { marginBottom: '15px' } },
                                        React.createElement('div', { style: { fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', opacity: 0.8 } }, `Ban History (${bannedUser.banHistory.length} events)`),
                                        React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '5px' } },
                                            bannedUser.banHistory.map((event, idx) =>
                                                React.createElement('div', {
                                                    key: idx,
                                                    style: {
                                                        fontSize: '11px',
                                                        padding: '6px 10px',
                                                        background: 'rgba(0,0,0,0.2)',
                                                        borderRadius: '6px',
                                                        display: 'flex',
                                                        justifyContent: 'space-between'
                                                    }
                                                },
                                                    React.createElement('span', {},
                                                        event.action === 'banned' ? '🔴 Banned' : '🟢 Unbanned'
                                                    ),
                                                    React.createElement('span', { style: { opacity: 0.6 } },
                                                        formatDate(event.bannedAt || event.unbannedAt)
                                                    )
                                                )
                                            )
                                        )
                                    ),

                                    // All Reports
                                    React.createElement('div', {},
                                        React.createElement('div', { style: { fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', opacity: 0.8 } }, `All Reports (${reports.length})`),
                                        reports.length === 0 ? (
                                            React.createElement('div', { style: { fontSize: '11px', opacity: 0.5, fontStyle: 'italic' } }, 'Loading reports...')
                                        ) : (
                                            React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '8px' } },
                                                reports.map(report =>
                                                    React.createElement('div', {
                                                        key: report.id,
                                                        style: {
                                                            background: 'rgba(0,0,0,0.2)',
                                                            padding: '10px',
                                                            borderRadius: '8px',
                                                            fontSize: '12px'
                                                        }
                                                    },
                                                        React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', marginBottom: '6px' } },
                                                            React.createElement('span', { style: { fontSize: '11px', opacity: 0.7 } },
                                                                `Reported by ${report.reporterName}`
                                                            ),
                                                            React.createElement('span', { style: { fontSize: '10px', opacity: 0.5 } },
                                                                report.timestamp?.seconds ? new Date(report.timestamp.seconds * 1000).toLocaleDateString() : 'Unknown'
                                                            )
                                                        ),
                                                        React.createElement('div', { style: { fontSize: '11px' } },
                                                            `"${report.reason}"`
                                                        ),
                                                        React.createElement('div', { style: { fontSize: '10px', opacity: 0.5, marginTop: '4px' } },
                                                            `Status: ${report.status}`
                                                        )
                                                    )
                                                )
                                            )
                                        )
                                    )
                                )
                            )
                        )
                    })
                )
            )
        ),

        React.createElement('div', { style: { marginTop: '50px', textAlign: 'center', opacity: 0.3, fontSize: '10px' } },
            `Admin ID: ${user.uid}`
        ),

        // Appeal Panel Removed (Now Inline)
    );
}
